import { loadCatalog, OcppError, ProtocolCatalog } from "./catalog";
import { ChargingProfileStore } from "./charging-profiles";
import { ConfigurationStore, SettingsKey, SUPPORTED_MEASURANDS } from "./configuration";
import { exampleCsmsRequest } from "./examples";
import { createFileTransfer, FileTransfer, HttpUploadMethod } from "./file-transfer";
import { isOcpp2, OcppProtocol, PROTOCOL_LABELS } from "./protocol";
import { OcppRpcConnection, randomMessageId, RpcFrameLog, WebSocketLike } from "./rpc";
import { buildMinimalPayload } from "./schema-defaults";
import { createV16Handlers } from "./adapters/v16";
import { createV2xHandlers } from "./adapters/v2x";

// Connector status as the UI shows it (the OCPP 1.6 ChargePointStatus set); 2.x reports the
// coarser ConnectorStatusEnum derived from it.
export type ConnectorStatus =
  | "Available"
  | "Preparing"
  | "Charging"
  | "SuspendedEV"
  | "SuspendedEVSE"
  | "Finishing"
  | "Reserved"
  | "Unavailable"
  | "Faulted";

export type ConnectionState = "disconnected" | "connecting" | "connected" | "accepted" | "pending" | "rejected" | "error";

export interface Transaction {
  /** 1.6: the CSMS-assigned integer as a string; 2.x: our own UUID. */
  id: string;
  /** 1.6 integer id (0 until StartTransaction is answered). */
  numericId?: number;
  idTag?: string;
  startedAt: string;
  meterStartWh: number;
  seqNo: number;
  chargingState: string;
  remoteStartId?: number;
  reservationId?: number;
  authorized: boolean;
  totalCost?: number;
  /** Transaction.Begin meter values, for the 1.6 StopTransaction transactionData. */
  beginValues?: any[];
}

export interface Reservation {
  id: number;
  idTag: string;
  expiresAt: string;
}

export interface EvseState {
  id: number;
  status: ConnectorStatus;
  operative: boolean;
  /** Inoperative requested while charging: applied once the transaction ends. */
  pendingInoperative: boolean;
  meterWh: number;
  soc: number;
  powerW: number;
  transaction?: Transaction;
  reservation?: Reservation;
  stopReason: string;
  unlockResult: "Unlocked" | "UnlockFailed";
  /** Charging power asked for by hand (W); the EV never draws more than the offered power. */
  requestedPowerW?: number;
  /** Suspended state set by hand (UI): the meter then delivers no power. */
  manualSuspend?: "SuspendedEV" | "SuspendedEVSE";
  /** When the meter was last advanced (ms). */
  meterUpdatedAt?: number;
  /** Register value at the previous periodic sample, for Energy.Active.Import.Interval. */
  intervalStartWh?: number;
}

export interface LogEntry {
  time: Date;
  type: "socket" | "send" | "receive" | "request" | "info" | "error";
  command?: string;
  message: string;
}

export interface BootNotificationSettings {
  chargePointVendor: string;
  chargePointModel: string;
  chargePointSerialNumber?: string;
  chargeBoxSerialNumber?: string;
  firmwareVersion?: string;
  iccid?: string;
  imsi?: string;
  meterType?: string;
  meterSerialNumber?: string;
}

export interface SimulationSettings {
  /** Uploaded file name; `{id}`, `{type}` and `{time}` are filled in. */
  diagnosticFileName: string;
  /** Extra seconds to wait before each upload attempt (0: upload right away). */
  diagnosticUploadTime: number;
  /** "Auto": the real upload result; "UploadFailed": force a failure. */
  diagnosticStatus: string;
  /** HTTP(S) uploads: POST multipart/form-data (field `file`) or PUT of the raw file. */
  httpUploadMethod?: HttpUploadMethod;
  /** "Installed": the real result; DownloadFailed / InvalidSignature / InstallationFailed: force it. */
  firmWareStatus: string;
  connectorOneUnlock?: "Unlocked" | "UnlockFailed";
  connectorTwoUnlock?: "Unlocked" | "UnlockFailed";
  /** Maximum charging power of a connector (W). */
  maxPowerW?: number;
  /** Battery of the simulated EV (Wh) and its state of charge when it plugs in (%). */
  batteryCapacityWh?: number;
  initialSoc?: number;
}

export interface SimulatorSettings {
  centralSystemUrl: string;
  chargePointId: string;
  protocol: OcppProtocol;
  numberOfConnectors: number;
  idTag: string;
  bootNotification: BootNotificationSettings;
  configuration: SettingsKey[];
  simulation: SimulationSettings;
  basicAuthPassword?: string;
  autoReconnect?: boolean;
  /** @deprecated use simulation.maxPowerW */
  powerW?: number;
}

export type SocketFactory = (url: string, protocols: string[], auth?: { username: string; password: string }) => WebSocketLike;

export interface SimulatorDeps {
  createSocket: SocketFactory;
  now?: () => Date;
  /** Delay between simulated firmware / upload steps (default 2 s). */
  stepDelayMs?: number;
  callTimeoutMs?: number;
  /** Uploads and downloads (default: HTTP(S) through fetch). */
  transfer?: FileTransfer;
}

export interface TransferState {
  kind: "upload" | "download";
  label: string;
  secondsLeft: number;
}

type Listener<T> = (value: T) => void;

const MAX_RECONNECT_DELAY_MS = 30_000;
const DEFAULT_REGISTRATION_RETRY_SECONDS = 30;
const MAX_HISTORY = 5000;
// A three-phase AC connector on a 230 V grid.
const NOMINAL_VOLTAGE = 230;
const PHASES = 3;
const DEFAULT_MAX_POWER_W = 11_000;
const DEFAULT_BATTERY_WH = 60_000;
const DEFAULT_INITIAL_SOC = 20;
const CHARGING_STATES: ConnectorStatus[] = ["Charging", "SuspendedEV", "SuspendedEVSE"];

export const toConnectorStatus2x = (status: ConnectorStatus): string => {
  switch (status) {
    case "Available":
    case "Reserved":
    case "Unavailable":
    case "Faulted":
      return status;
    default:
      return "Occupied";
  }
};

/**
 * A simulated OCPP charge point (1.6J, 2.0.1 or 2.1). Holds all charge-point state and speaks the
 * selected version through an OcppRpcConnection; version-specific CSMS requests are answered by
 * the handlers in adapters/. UI or headless code drives it through the public methods and
 * listens with `on()`.
 */
export class ChargePointSimulator {
  readonly settings: SimulatorSettings;
  readonly protocol: OcppProtocol;
  readonly config: ConfigurationStore;
  readonly profiles = new ChargingProfileStore();
  readonly evses: EvseState[];
  readonly localList = { version: 0, entries: new Map<string, any>() };
  readonly certificates: Array<{ certificateType: string; certificate: string; hashData: any }> = [];
  readonly displayMessages = new Map<number, any>();
  readonly monitors = new Map<number, any>();
  readonly networkProfiles = new Map<number, any>();
  readonly records: Record<string, any> = {};
  pendingReset: string | null = null;
  publishedFirmware: { checksum: string; location: string } | null = null;

  private readonly deps: SimulatorDeps;
  private readonly now: () => Date;
  private catalog: ProtocolCatalog | null = null;
  private socket: WebSocketLike | null = null;
  private rpc: OcppRpcConnection | null = null;
  private state: ConnectionState = "disconnected";
  private manualDisconnect = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private registrationTimer: ReturnType<typeof setTimeout> | null = null;
  private meterTimers = new Map<number, ReturnType<typeof setInterval>>();
  private clockTimer: ReturnType<typeof setTimeout> | null = null;
  private physicsTimer: ReturnType<typeof setInterval> | null = null;
  private connectionTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private nextBootHooks: Array<() => void | Promise<void>> = [];
  private reservationTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private pendingTimers = new Set<ReturnType<typeof setTimeout>>();
  private offlineQueue: Array<{ action: string; params: any }> = [];
  private bootReason = "PowerUp";
  private listeners: { [K in keyof Events]: Array<Listener<Events[K]>> } = {
    log: [],
    state: [],
    evse: [],
    upload: [],
    config: [],
  };
  private handlers: Record<string, (params: any) => Promise<any>> = {};
  authorizedIdTag: string | null = null;
  readonly transfer: FileTransfer;
  /** Everything logged, for the diagnostics file (most recent MAX_HISTORY entries). */
  readonly history: LogEntry[] = [];
  activeUpload: { cancelled: boolean } | null = null;
  activeFirmware: { cancelled: boolean } | null = null;

  constructor(settings: SimulatorSettings, deps: SimulatorDeps) {
    this.settings = settings;
    this.protocol = settings.protocol;
    this.deps = deps;
    this.now = deps.now ?? (() => new Date());
    this.transfer = deps.transfer ?? createFileTransfer({ uploadMethod: () => this.settings.simulation.httpUploadMethod ?? "POST" });
    const evseIds = Array.from({ length: Math.max(1, settings.numberOfConnectors) }, (_, i) => i + 1);
    this.config = new ConfigurationStore(settings.configuration, {
      identity: settings.chargePointId,
      numberOfConnectors: evseIds.length,
      evseIds,
      fileTransferProtocols: this.transfer.protocols,
    });
    this.evses = evseIds.map(id => ({
      id,
      status: "Available",
      operative: true,
      pendingInoperative: false,
      meterWh: 0,
      soc: this.initialSoc,
      powerW: 0,
      stopReason: "Remote",
      unlockResult: (id === 2 ? settings.simulation.connectorTwoUnlock : settings.simulation.connectorOneUnlock) ?? "Unlocked",
    }));
    this.config.onChange(entry => {
      if (entry.key === "HeartbeatInterval") this.startHeartbeat();
      if (entry.key === "MeterValueSampleInterval") this.evses.filter(e => e.transaction).forEach(e => this.startMeterTimer(e));
      if (entry.key === "ClockAlignedDataInterval") this.startClockAlignedTimer();
      this.emit("config", this.config.all());
    });
  }

  // ---- events ------------------------------------------------------------------------------

  on<K extends keyof Events>(event: K, listener: Listener<Events[K]>): () => void {
    (this.listeners[event] as Array<Listener<Events[K]>>).push(listener);
    return () => {
      this.listeners[event] = (this.listeners[event] as Array<Listener<Events[K]>>).filter(l => l !== listener) as any;
    };
  }

  private emit<K extends keyof Events>(event: K, value: Events[K]) {
    (this.listeners[event] as Array<Listener<Events[K]>>).forEach(listener => listener(value));
  }

  log(type: LogEntry["type"], message: string, command?: string) {
    const entry = { time: this.now(), type, message, command };
    this.history.push(entry);
    if (this.history.length > MAX_HISTORY) this.history.splice(0, this.history.length - MAX_HISTORY);
    this.emit("log", entry);
  }

  get connectionState() {
    return this.state;
  }

  get isOnline() {
    return this.state === "accepted" || this.state === "pending";
  }

  get catalogOrThrow(): ProtocolCatalog {
    if (!this.catalog) throw new Error("OCPP schemas not loaded yet");
    return this.catalog;
  }

  private setState(state: ConnectionState) {
    this.state = state;
    this.emit("state", state);
  }

  evse(id: number): EvseState | undefined {
    return this.evses.find(evse => evse.id === id);
  }

  emitEvse(evse: EvseState) {
    this.emit("evse", { ...evse, transaction: evse.transaction && { ...evse.transaction } });
  }

  get uploading() {
    return this.activeUpload !== null;
  }

  /** The file transfer in progress, for the UI (null: none). */
  setTransfer(state: TransferState | null) {
    this.emit("upload", state ? { active: true, ...state } : { active: false, kind: "upload", label: "", secondsLeft: 0 });
  }

  /** Resolves after `ms`; never resolves once the simulator is disposed. */
  delay(ms: number) {
    return new Promise<void>(resolve => this.later(resolve, ms));
  }

  /** Run `hook` after the next accepted BootNotification (e.g. FirmwareStatus Installed). */
  onNextBoot(hook: () => void | Promise<void>) {
    this.nextBootHooks.push(hook);
  }

  // ---- connection --------------------------------------------------------------------------

  async connect(): Promise<void> {
    this.manualDisconnect = false;
    this.clearReconnect();
    this.catalog = await loadCatalog(this.protocol);
    this.handlers = isOcpp2(this.protocol) ? createV2xHandlers(this) : createV16Handlers(this);
    this.openSocket();
  }

  disconnect(): void {
    this.manualDisconnect = true;
    this.clearReconnect();
    this.socket?.close(1000, "Disconnected by user");
  }

  /** Stop everything (tests, unmount). */
  dispose(): void {
    this.disconnect();
    this.stopTimers();
    if (this.physicsTimer) clearInterval(this.physicsTimer);
    this.physicsTimer = null;
    this.connectionTimers.forEach(timer => clearTimeout(timer));
    this.connectionTimers.clear();
    this.pendingTimers.forEach(timer => clearTimeout(timer));
    this.pendingTimers.clear();
    this.reservationTimers.forEach(timer => clearTimeout(timer));
    this.reservationTimers.clear();
  }

  private openSocket() {
    const url = `${this.settings.centralSystemUrl.trim().replace(/\/+$/, "")}/${encodeURIComponent(this.settings.chargePointId)}`;
    this.setState("connecting");
    this.log("socket", `Connecting to ${url} (${PROTOCOL_LABELS[this.protocol]})`);
    const auth = this.settings.basicAuthPassword
      ? { username: this.settings.chargePointId, password: this.settings.basicAuthPassword }
      : undefined;
    const socket = this.deps.createSocket(url, [this.protocol], auth);
    this.socket = socket;

    socket.onopen = () => {
      if (socket.protocol && socket.protocol !== this.protocol) {
        this.log("error", `Central system selected ${socket.protocol}, expected ${this.protocol}`);
        socket.close(1002, "Unsupported subprotocol");
        return;
      }
      this.reconnectAttempts = 0;
      this.rpc = new OcppRpcConnection({
        socket,
        catalog: this.catalogOrThrow,
        onCall: (action, params) => this.handleCall(action, params),
        onFrame: entry => this.logFrame(entry),
        callTimeoutMs: this.deps.callTimeoutMs,
        newMessageId: randomMessageId,
      });
      this.setState("connected");
      this.log("socket", "Charge point connected");
      void this.boot();
    };
    socket.onmessage = event => this.rpc?.receive(event.data);
    socket.onerror = () => this.log("error", "WebSocket error");
    socket.onclose = event => {
      if (this.socket !== socket) return;
      this.rpc?.close();
      this.rpc = null;
      this.socket = null;
      this.stopTimers();
      const abnormal = event.code !== 1000;
      this.log(abnormal ? "error" : "socket", abnormal ? `Connection lost (${event.code}${event.reason ? `: ${event.reason}` : ""})` : "Charge point disconnected");
      this.setState(abnormal ? "error" : "disconnected");
      if (!this.manualDisconnect && this.settings.autoReconnect !== false) this.scheduleReconnect();
    };
  }

  private scheduleReconnect(delayMs?: number) {
    if (this.reconnectTimer) return;
    const delay = delayMs ?? Math.min(2 ** this.reconnectAttempts * 1000, MAX_RECONNECT_DELAY_MS);
    this.reconnectAttempts += 1;
    this.log("socket", `Reconnecting in ${Math.round(delay / 1000)}s`);
    this.setState("connecting");
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  private clearReconnect() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  /** Close and reconnect, e.g. after a Reset; the next boot reports `reason`. */
  reboot(reason: string, delayMs = 1000) {
    this.bootReason = reason;
    const socket = this.socket;
    this.later(() => {
      if (!socket) return;
      this.socket = null;
      this.rpc?.close();
      this.rpc = null;
      this.stopTimers();
      socket.onclose = null;
      socket.close(1000, "Rebooting");
      this.log("socket", `Rebooting (${reason})`);
      this.setState("connecting");
      this.scheduleReconnect(delayMs);
    }, 0);
  }

  private stopTimers() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    if (this.registrationTimer) clearTimeout(this.registrationTimer);
    this.registrationTimer = null;
    this.meterTimers.forEach(timer => clearInterval(timer));
    this.meterTimers.clear();
    if (this.clockTimer) clearTimeout(this.clockTimer);
    this.clockTimer = null;
  }

  /** Run `fn` after `ms` (0: after the current CALLRESULT has been sent). */
  later(fn: () => void | Promise<void>, ms = 0) {
    const timer = setTimeout(() => {
      this.pendingTimers.delete(timer);
      Promise.resolve(fn()).catch(error => this.log("error", String(error?.message ?? error)));
    }, ms);
    this.pendingTimers.add(timer);
  }

  get stepDelayMs() {
    return this.deps.stepDelayMs ?? 2000;
  }

  // ---- outgoing ----------------------------------------------------------------------------

  /** Send a charge-point CALL; resolves with the CSMS answer. */
  async call(action: string, params: any): Promise<any> {
    if (!this.rpc) throw new OcppError("GenericError", `Cannot send ${action}: not connected`);
    if (this.state === "rejected" && action !== "BootNotification") {
      throw new OcppError("SecurityError", `Registration rejected: only BootNotification may be sent`);
    }
    try {
      return await this.rpc.call(action, params);
    } catch (error: any) {
      this.log("error", `${action}: ${error.code ?? "Error"} ${error.message}`, action);
      throw error;
    }
  }

  /**
   * Transaction messages are queued while offline and delivered after the next boot. A CSMS that
   * answers with an error or not at all gets TransactionMessageAttempts tries, each
   * TransactionMessageRetryInterval x attempt seconds after the previous one.
   */
  async callOrQueue(action: string, params: any): Promise<any> {
    const queue = () => {
      const queued = isOcpp2(this.protocol) && action === "TransactionEvent" ? { ...params, offline: true } : params;
      this.offlineQueue.push({ action, params: queued });
      this.log("info", `Offline: queued ${action}`, action);
      return null;
    };
    if (!this.rpc || !this.isOnline) return queue();
    const attempts = Math.max(1, this.config.getNumber("TransactionMessageAttempts", 3));
    const intervalS = Math.max(0, this.config.getNumber("TransactionMessageRetryInterval", 60));
    for (let attempt = 1; ; attempt++) {
      try {
        return await this.call(action, params);
      } catch (error) {
        if (!this.rpc || !this.isOnline) return queue();
        if (attempt >= attempts) throw error;
        this.log("info", `${action}: attempt ${attempt}/${attempts} failed, retrying in ${intervalS * attempt}s`, action);
        await this.delay(intervalS * attempt * 1000);
      }
    }
  }

  sendSend(action: string, params: any) {
    if (!this.rpc) throw new OcppError("GenericError", `Cannot send ${action}: not connected`);
    this.rpc.send(action, params);
  }

  /** Send any charge-point message with a raw payload (the UI's "custom message"). */
  async sendRaw(action: string, params: any): Promise<any> {
    if (this.catalogOrThrow.sendMessages.has(action)) {
      this.sendSend(action, params);
      return null;
    }
    return this.call(action, params);
  }

  /** A schema-valid example payload for a charge-point message. */
  examplePayload(action: string): any {
    const schema = this.catalogOrThrow.schema(action, "req");
    return schema ? buildMinimalPayload(schema, { now: this.now }) : {};
  }

  get chargePointActions(): string[] {
    return this.catalog ? [...this.catalog.cpInitiated].sort() : [];
  }

  /** Messages a CSMS may send to this version, for testing them locally (injectCsmsCall). */
  get csmsActions(): string[] {
    return this.catalog ? [...this.catalog.csmsInitiated].sort() : [];
  }

  /** A realistic, schema-valid example of a CSMS request (falls back to the minimal payload). */
  exampleCsmsPayload(action: string): any {
    const example = exampleCsmsRequest(this, action);
    if (example !== undefined) return example;
    const schema = this.catalogOrThrow.schema(action, "req");
    return schema ? buildMinimalPayload(schema, { now: this.now }) : {};
  }

  /**
   * Handle a CSMS request as if the CSMS had sent it (schema-checked both ways), e.g. to try
   * GetDiagnostics without a CSMS that can send it. Follow-up messages (status notifications,
   * transactions, ...) still go to the connected CSMS.
   */
  async injectCsmsCall(action: string, params: any): Promise<any> {
    const catalog = this.catalogOrThrow;
    if (!catalog.csmsInitiated.has(action)) throw new OcppError("NotSupported", `${action} is not sent by a CSMS in ${this.protocol}`);
    catalog.validate(action, "req", params);
    this.log("request", `(local test) ${JSON.stringify(params)}`, action);
    const response = await this.dispatch(action, params);
    catalog.validate(action, "conf", response);
    this.log("send", `(local test) ${JSON.stringify(response)}`, action);
    return response;
  }

  timestamp() {
    return this.now().toISOString();
  }

  bootPayload() {
    const boot = this.settings.bootNotification;
    if (!isOcpp2(this.protocol)) {
      return Object.fromEntries(Object.entries(boot).filter(([, value]) => value !== undefined && value !== ""));
    }
    const modem = { ...(boot.iccid && { iccid: boot.iccid }), ...(boot.imsi && { imsi: boot.imsi }) };
    return {
      reason: this.bootReason,
      chargingStation: {
        model: boot.chargePointModel,
        vendorName: boot.chargePointVendor,
        ...(boot.chargePointSerialNumber && { serialNumber: boot.chargePointSerialNumber }),
        ...(boot.firmwareVersion && { firmwareVersion: boot.firmwareVersion }),
        ...(Object.keys(modem).length > 0 && { modem }),
      },
    };
  }

  async boot(): Promise<void> {
    let response: any;
    try {
      response = await this.call("BootNotification", this.bootPayload());
    } catch {
      this.registrationTimer = setTimeout(() => void this.boot(), DEFAULT_REGISTRATION_RETRY_SECONDS * 1000);
      return;
    }
    const interval = Number(response.interval) > 0 ? Number(response.interval) : undefined;
    if (response.status === "Accepted") {
      this.bootReason = "PowerUp";
      if (interval) this.config.changeConfiguration16("HeartbeatInterval", String(interval));
      this.setState("accepted");
      this.startHeartbeat();
      await this.sendAllStatuses();
      await this.flushOfflineQueue();
      for (const evse of this.evses) if (evse.transaction) this.startMeterTimer(evse);
      this.startClockAlignedTimer();
      for (const hook of this.nextBootHooks.splice(0)) await hook();
      return;
    }
    // Pending: the CSMS may configure us, then sends Accepted on a later boot. Rejected: only
    // retry the boot. Either way retry after `interval`.
    this.setState(response.status === "Pending" ? "pending" : "rejected");
    this.log("info", `Registration ${response.status}; retrying in ${interval ?? DEFAULT_REGISTRATION_RETRY_SECONDS}s`, "BootNotification");
    this.registrationTimer = setTimeout(() => void this.boot(), (interval ?? DEFAULT_REGISTRATION_RETRY_SECONDS) * 1000);
  }

  private async flushOfflineQueue() {
    const queued = this.offlineQueue.splice(0);
    for (const { action, params } of queued) {
      await this.call(action, params).catch(() => undefined);
    }
  }

  startHeartbeat() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    const seconds = this.config.getNumber("HeartbeatInterval", 0);
    if (seconds <= 0 || this.state !== "accepted") return;
    this.heartbeatTimer = setInterval(() => void this.heartbeat().catch(() => undefined), seconds * 1000);
  }

  /**
   * Browsers expose no WebSocket ping/pong, so an unanswered Heartbeat is how a dead connection
   * is noticed: close it abnormally, which triggers the auto-reconnect.
   */
  async heartbeat() {
    try {
      return await this.call("Heartbeat", {});
    } catch (error: any) {
      if (error?.code === "Timeout" && this.socket) {
        this.log("error", "Heartbeat not answered: closing the connection", "Heartbeat");
        this.socket.close(4000, "Heartbeat timeout");
      }
      throw error;
    }
  }

  async sendAllStatuses() {
    if (!isOcpp2(this.protocol)) {
      // 1.6: connector 0 is the charge point itself.
      const anyOperative = this.evses.some(evse => evse.operative);
      await this.call("StatusNotification", {
        connectorId: 0,
        status: anyOperative ? "Available" : "Unavailable",
        errorCode: "NoError",
        timestamp: this.timestamp(),
      }).catch(() => undefined);
    }
    for (const evse of this.evses) await this.sendStatus(evse).catch(() => undefined);
  }

  async setStatus(evseId: number, status: ConnectorStatus, errorCode = "NoError") {
    const evse = this.evse(evseId);
    if (!evse) throw new Error(`Unknown connector ${evseId}`);
    if (evse.transaction && !CHARGING_STATES.includes(status)) {
      // Leaving Charging / Suspended* by hand ends the transaction (StopTransaction / TransactionEvent
      // Ended with the selected stop reason), so the connector is back to its idle controls.
      await this.stopTransaction(evseId);
      if (evse.status === status) return;
    }
    // Bank the energy delivered so far at the old power before the status changes.
    if (evse.transaction) this.advanceMeter(evse);
    if (evse.transaction && CHARGING_STATES.includes(status)) {
      // Pausing / resuming by hand: the EV (SuspendedEV) or the EVSE (SuspendedEVSE) stops drawing power.
      evse.manualSuspend = status === "Charging" ? undefined : (status as "SuspendedEV" | "SuspendedEVSE");
      this.updateChargingState(evse, false);
    }
    evse.status = status;
    if (evse.transaction) evse.powerW = this.drawnPowerW(evse);
    this.emitEvse(evse);
    this.watchConnectionTimeout(evse);
    if (this.isOnline) await this.sendStatus(evse, errorCode);
  }

  /**
   * ConnectionTimeOut: a connector left in Preparing (cable plugged / tag presented) without a
   * transaction starting goes back to Available.
   */
  private watchConnectionTimeout(evse: EvseState) {
    const existing = this.connectionTimers.get(evse.id);
    if (existing) clearTimeout(existing);
    this.connectionTimers.delete(evse.id);
    const seconds = this.config.getNumber("ConnectionTimeOut", 0);
    if (evse.status !== "Preparing" || evse.transaction || seconds <= 0) return;
    this.connectionTimers.set(
      evse.id,
      setTimeout(() => {
        this.connectionTimers.delete(evse.id);
        if (evse.status !== "Preparing" || evse.transaction) return;
        this.log("info", `Connector ${evse.id}: no transaction within ConnectionTimeOut (${seconds}s)`);
        void this.setStatus(evse.id, "Available").catch(() => undefined);
      }, seconds * 1000)
    );
  }

  async sendStatus(evse: EvseState, errorCode = evse.status === "Faulted" ? "OtherError" : "NoError") {
    if (!isOcpp2(this.protocol)) {
      return this.call("StatusNotification", {
        connectorId: evse.id,
        status: evse.status,
        errorCode,
        timestamp: this.timestamp(),
      });
    }
    return this.call("StatusNotification", {
      timestamp: this.timestamp(),
      connectorStatus: toConnectorStatus2x(evse.status),
      evseId: evse.id,
      connectorId: 1,
    });
  }

  // ---- authorisation -----------------------------------------------------------------------

  idToken(idTag: string, type = "ISO14443") {
    return { idToken: idTag, type };
  }

  localAuthorization(idTag: string): string | null {
    if (!this.config.getBoolean("LocalAuthListEnabled")) return null;
    const entry = this.localList.entries.get(idTag);
    if (!entry) return null;
    return (isOcpp2(this.protocol) ? entry.idTokenInfo?.status : entry.idTagInfo?.status) ?? null;
  }

  /** Authorize an id tag; resolves with the authorisation status. */
  async authorize(idTag = this.settings.idTag): Promise<string> {
    const local = this.config.getBoolean("LocalPreAuthorize") ? this.localAuthorization(idTag) : null;
    let status: string;
    if (local === "Accepted") {
      status = local;
      this.log("info", `${idTag} accepted from the local authorization list`, "Authorize");
    } else {
      const response = isOcpp2(this.protocol)
        ? await this.call("Authorize", { idToken: this.idToken(idTag) })
        : await this.call("Authorize", { idTag });
      status = (isOcpp2(this.protocol) ? response.idTokenInfo?.status : response.idTagInfo?.status) ?? "Invalid";
    }
    this.authorizedIdTag = status === "Accepted" ? idTag : null;
    this.emit("state", this.state);
    return status;
  }

  // ---- transactions ------------------------------------------------------------------------

  get initialSoc() {
    return Math.max(0, Math.min(100, Number(this.settings.simulation?.initialSoc ?? DEFAULT_INITIAL_SOC)));
  }

  private measurandsFor(context: string): string[] {
    const value =
      context === "Sample.Clock"
        ? this.config.get("MeterValuesAlignedData")
        : context === "Transaction.Begin"
          ? isOcpp2(this.protocol)
            ? this.config.findByRef({ name: "SampledDataCtrlr" }, { name: "TxStartedMeasurands" })?.value
            : this.config.get("MeterValuesSampledData")
          : context === "Transaction.End"
            ? this.config.get("StopTxnSampledData")
            : this.config.get("MeterValuesSampledData");
    return (value ?? "")
      .split(",")
      .map(item => item.trim())
      .filter(item => SUPPORTED_MEASURANDS.includes(item));
  }

  get maxPowerW() {
    return Number(this.settings.simulation?.maxPowerW ?? this.settings.powerW ?? DEFAULT_MAX_POWER_W);
  }

  /** Power the connector may deliver right now: hardware maximum capped by the charging profiles. */
  offeredPowerW(evse: EvseState) {
    const max = this.maxPowerW;
    const tx = evse.transaction;
    const elapsed = tx ? Math.max(0, (this.now().getTime() - new Date(tx.startedAt).getTime()) / 1000) : 0;
    const schedule = this.profiles.composite(evse.id, Math.ceil(elapsed) + 1, undefined, tx?.id);
    const period = schedule?.chargingSchedulePeriod.filter(p => p.startPeriod <= elapsed).pop();
    if (!schedule || !period) return max;
    const limitW = schedule.chargingRateUnit === "A" ? period.limit * NOMINAL_VOLTAGE * (period.numberPhases ?? PHASES) : period.limit;
    return Math.max(0, Math.min(max, limitW));
  }

  /** What the EV draws: the offered power up to 80 % state of charge, then tapering to 10 %. */
  private drawnPowerW(evse: EvseState) {
    if (!evse.transaction || !CHARGING_STATES.includes(evse.status) || evse.manualSuspend || evse.soc >= 100) return 0;
    const taper = evse.soc <= 80 ? 1 : Math.max(0.1, 1 - ((evse.soc - 80) / 20) * 0.9);
    const max = this.maxPowerW;
    const wanted = evse.requestedPowerW ?? max;
    return Math.max(0, Math.min(this.offeredPowerW(evse), wanted * taper));
  }

  /** Integrate energy and state of charge since the last update, then set the new power. */
  advanceMeter(evse: EvseState) {
    const nowMs = this.now().getTime();
    const seconds = evse.meterUpdatedAt === undefined ? 0 : Math.max(0, (nowMs - evse.meterUpdatedAt) / 1000);
    evse.meterUpdatedAt = nowMs;
    if (!evse.transaction) {
      evse.powerW = 0;
      return;
    }
    const energyWh = (evse.powerW * seconds) / 3600;
    evse.meterWh += energyWh;
    const capacity = Number(this.settings.simulation?.batteryCapacityWh ?? DEFAULT_BATTERY_WH);
    if (capacity > 0) evse.soc = Math.min(100, evse.soc + (energyWh / capacity) * 100);
    evse.powerW = this.drawnPowerW(evse);
    this.updateChargingState(evse, true);
  }

  /** Charging / SuspendedEV / SuspendedEVSE follow the power the EV actually draws. */
  private updateChargingState(evse: EvseState, notify: boolean) {
    const tx = evse.transaction;
    if (!tx || !CHARGING_STATES.includes(evse.status)) return;
    const next: ConnectorStatus =
      evse.manualSuspend ?? (evse.powerW > 0 ? "Charging" : evse.soc >= 100 ? "SuspendedEV" : "SuspendedEVSE");
    if (next === evse.status && next === tx.chargingState) return;
    const changed = next !== evse.status;
    evse.status = next;
    tx.chargingState = next;
    if (!notify || !changed || !this.isOnline) return;
    this.log("info", `Connector ${evse.id}: ${next}`);
    this.later(async () => {
      if (!isOcpp2(this.protocol)) {
        await this.sendStatus(evse).catch(() => undefined);
        return;
      }
      await this.callOrQueue("TransactionEvent", {
        eventType: "Updated",
        timestamp: this.timestamp(),
        triggerReason: "ChargingStateChanged",
        seqNo: tx.seqNo++,
        evse: { id: evse.id, connectorId: 1 },
        transactionInfo: { transactionId: tx.id, chargingState: next },
      }).catch(() => undefined);
    });
  }

  private startPhysics() {
    if (this.physicsTimer) return;
    this.physicsTimer = setInterval(() => {
      const charging = this.evses.filter(evse => evse.transaction);
      if (charging.length === 0) {
        clearInterval(this.physicsTimer!);
        this.physicsTimer = null;
        return;
      }
      for (const evse of charging) {
        this.advanceMeter(evse);
        this.emitEvse(evse);
      }
    }, 1000);
  }

  sampledValues(evse: EvseState, context: string, measurands = this.measurandsFor(context)) {
    const noise = (spread: number) => (Math.random() - 0.5) * 2 * spread;
    const power = evse.powerW;
    const voltage = () => Number((NOMINAL_VOLTAGE + noise(2)).toFixed(1));
    const current = power > 0 ? power / NOMINAL_VOLTAGE / PHASES : 0;
    const offered = this.offeredPowerW(evse);
    const phases = ["L1", "L2", "L3"];
    type Sample = { measurand: string; value: number; unit?: string; phase?: string; location?: string };
    const samples: Sample[] = measurands.flatMap((measurand): Sample[] => {
      switch (measurand) {
        case "Energy.Active.Import.Register":
          return [{ measurand, value: Math.round(evse.meterWh), unit: "Wh" }];
        case "Energy.Active.Export.Register":
          return [{ measurand, value: 0, unit: "Wh" }];
        case "Energy.Reactive.Import.Register":
          return [{ measurand, value: Math.round(evse.meterWh * 0.02), unit: "varh" }];
        case "Energy.Active.Import.Interval":
          return [{ measurand, value: Math.round(evse.meterWh - (evse.intervalStartWh ?? evse.transaction?.meterStartWh ?? evse.meterWh)), unit: "Wh" }];
        case "Power.Active.Import":
          return [{ measurand, value: Math.round(power), unit: "W" }];
        case "Power.Offered":
          return [{ measurand, value: Math.round(offered), unit: "W" }];
        case "Power.Factor":
          return [{ measurand, value: power > 0 ? 0.99 : 1 }];
        case "Current.Import":
          return phases.map(phase => ({ measurand, value: Number(Math.max(0, current + (current > 0 ? noise(0.05) : 0)).toFixed(2)), unit: "A", phase }));
        case "Current.Offered":
          return [{ measurand, value: Number((offered / NOMINAL_VOLTAGE / PHASES).toFixed(1)), unit: "A" }];
        case "Voltage":
          return phases.map(phase => ({ measurand, value: voltage(), unit: "V", phase: `${phase}-N` }));
        case "Frequency":
          // 1.6 has no unit for frequency before the errata, so it is left out there.
          return [{ measurand, value: Number((50 + noise(0.03)).toFixed(2)), ...(isOcpp2(this.protocol) && { unit: "Hertz" }) }];
        case "Temperature":
          return [{ measurand, value: Number((25 + power / 1000 + noise(0.5)).toFixed(1)), unit: "Celsius", location: "Body" }];
        case "SoC":
          return [{ measurand, value: Math.round(evse.soc), unit: "Percent", location: "EV" }];
        default:
          return [];
      }
    });
    if (context === "Sample.Periodic" || context === "Sample.Clock") evse.intervalStartWh = evse.meterWh;
    if (!isOcpp2(this.protocol)) {
      return samples.map(({ value, ...rest }) => ({ value: String(value), context, ...rest }));
    }
    return samples.map(({ value, unit, ...rest }) => ({ value, context, ...(unit && { unitOfMeasure: { unit } }), ...rest }));
  }

  /** One MeterValue, or none when no measurand is configured for the context. */
  meterValue(evse: EvseState, context: string, measurands?: string[]) {
    const sampledValue = this.sampledValues(evse, context, measurands);
    return sampledValue.length > 0 ? [{ timestamp: this.timestamp(), sampledValue }] : [];
  }

  /** Begin charging on `evseId` for `idTag`. Resolves true when the transaction is accepted. */
  async startTransaction(
    evseId: number,
    options: { idTag?: string; remoteStartId?: number; triggerReason?: string; chargingProfile?: any } = {}
  ): Promise<boolean> {
    const evse = this.evse(evseId);
    if (!evse) throw new Error(`Unknown connector ${evseId}`);
    if (evse.transaction) throw new Error(`Connector ${evseId} already has a transaction`);
    const idTag = options.idTag ?? this.authorizedIdTag ?? this.settings.idTag;
    const reservationId = evse.reservation && evse.reservation.idTag === idTag ? evse.reservation.id : undefined;
    if (evse.reservation && reservationId === undefined) throw new Error(`Connector ${evseId} is reserved for another id tag`);
    if (reservationId !== undefined) this.clearReservation(evse, false);

    const connectionTimer = this.connectionTimers.get(evse.id);
    if (connectionTimer) clearTimeout(connectionTimer);
    this.connectionTimers.delete(evse.id);
    evse.manualSuspend = undefined;
    evse.intervalStartWh = evse.meterWh;
    const tx: Transaction = {
      id: isOcpp2(this.protocol) ? randomMessageId() : "0",
      idTag,
      startedAt: this.timestamp(),
      meterStartWh: Math.round(evse.meterWh),
      seqNo: 0,
      chargingState: "Charging",
      remoteStartId: options.remoteStartId,
      reservationId,
      authorized: true,
    };
    evse.transaction = tx;
    evse.status = "Charging";
    evse.meterUpdatedAt = undefined;
    this.advanceMeter(evse);
    const beginValues = this.meterValue(evse, "Transaction.Begin");
    this.emitEvse(evse);

    let accepted: boolean;
    if (!isOcpp2(this.protocol)) {
      const response = await this.call("StartTransaction", {
        connectorId: evse.id,
        idTag,
        meterStart: tx.meterStartWh,
        timestamp: tx.startedAt,
        ...(reservationId !== undefined && { reservationId }),
      });
      tx.numericId = response.transactionId;
      tx.id = String(response.transactionId);
      accepted = response.idTagInfo?.status === "Accepted";
    } else {
      const response = await this.call("TransactionEvent", {
        eventType: "Started",
        timestamp: tx.startedAt,
        triggerReason: options.triggerReason ?? (options.remoteStartId !== undefined ? "RemoteStart" : "Authorized"),
        seqNo: tx.seqNo++,
        evse: { id: evse.id, connectorId: 1 },
        idToken: this.idToken(idTag, options.remoteStartId !== undefined ? "Central" : "ISO14443"),
        transactionInfo: {
          transactionId: tx.id,
          chargingState: tx.chargingState,
          ...(options.remoteStartId !== undefined && { remoteStartId: options.remoteStartId }),
        },
        ...(reservationId !== undefined && { reservationId }),
        ...(beginValues.length > 0 && { meterValue: beginValues }),
      });
      accepted = !response.idTokenInfo || response.idTokenInfo.status === "Accepted";
    }
    if (options.chargingProfile) {
      this.profiles.install(evse.id, { ...options.chargingProfile, transactionId: tx.id });
    }
    this.emitEvse(evse);
    await this.sendStatus(evse).catch(() => undefined);

    if (!accepted) {
      this.log("info", `Transaction ${tx.id} not authorised by the CSMS`, isOcpp2(this.protocol) ? "TransactionEvent" : "StartTransaction");
      if (this.config.getBoolean("StopTransactionOnInvalidId")) await this.stopTransaction(evse.id, "DeAuthorized");
      return false;
    }
    tx.beginValues = beginValues;
    this.startMeterTimer(evse);
    this.startPhysics();
    return true;
  }

  /** Transaction.Begin samples of the StopTxnSampledData measurands, for transactionData. */
  private beginValuesFor(tx: Transaction) {
    const measurands = this.measurandsFor("Transaction.End");
    return (tx.beginValues ?? [])
      .map(value => ({ ...value, sampledValue: value.sampledValue.filter((sample: any) => measurands.includes(sample.measurand)) }))
      .filter(value => value.sampledValue.length > 0);
  }

  private triggerReasonFor(reason: string) {
    const map: Record<string, string> = {
      Remote: "RemoteStop",
      Local: "StopAuthorized",
      EVDisconnected: "EVCommunicationLost",
      DeAuthorized: "Deauthorized",
      EmergencyStop: "AbnormalCondition",
      ImmediateReset: "ResetCommand",
      HardReset: "ResetCommand",
      SoftReset: "ResetCommand",
      UnlockCommand: "UnlockCommand",
      PowerLoss: "AbnormalCondition",
      Reboot: "ResetCommand",
    };
    return map[reason] ?? "StopAuthorized";
  }

  private stoppedReason2x(reason: string) {
    const map: Record<string, string> = { HardReset: "ImmediateReset", SoftReset: "Reboot", UnlockCommand: "Other" };
    return map[reason] ?? reason;
  }

  async stopTransaction(evseId: number, reason = this.evse(evseId)?.stopReason ?? "Local"): Promise<void> {
    const evse = this.evse(evseId);
    if (!evse?.transaction) throw new Error(`Connector ${evseId} has no transaction`);
    const tx = evse.transaction;
    this.stopMeterTimer(evse);
    this.advanceMeter(evse);
    evse.powerW = 0;
    const endValues = this.meterValue(evse, "Transaction.End");

    if (!isOcpp2(this.protocol)) {
      await this.callOrQueue("StopTransaction", {
        transactionId: tx.numericId ?? Number(tx.id),
        meterStop: Math.round(evse.meterWh),
        timestamp: this.timestamp(),
        reason,
        ...(tx.idTag && { idTag: tx.idTag }),
        // StopTxnSampledData: the configured measurands at the start and the end of the transaction.
        ...(endValues.length > 0 && { transactionData: [...this.beginValuesFor(tx), ...endValues] }),
      }).catch(() => undefined);
    } else {
      await this.callOrQueue("TransactionEvent", {
        eventType: "Ended",
        timestamp: this.timestamp(),
        triggerReason: this.triggerReasonFor(reason),
        seqNo: tx.seqNo++,
        evse: { id: evse.id, connectorId: 1 },
        transactionInfo: { transactionId: tx.id, chargingState: "EVConnected", stoppedReason: this.stoppedReason2x(reason) },
        ...(endValues.length > 0 && { meterValue: endValues }),
      }).catch(() => undefined);
    }

    this.profiles.endTransaction(tx.id);
    evse.transaction = undefined;
    evse.manualSuspend = undefined;
    // The EV leaves; the next one plugs in with the configured state of charge.
    evse.soc = this.initialSoc;
    evse.status = evse.pendingInoperative ? "Unavailable" : "Finishing";
    if (evse.pendingInoperative) {
      evse.operative = false;
      evse.pendingInoperative = false;
    }
    this.emitEvse(evse);
    if (this.isOnline) await this.sendStatus(evse).catch(() => undefined);
    if (this.pendingReset && this.evses.every(e => !e.transaction)) {
      const type = this.pendingReset;
      this.pendingReset = null;
      this.reboot(type === "OnIdle" ? "RemoteReset" : type);
    }
  }

  async sendMeterValues(evseId: number, context = "Sample.Periodic"): Promise<void> {
    const evse = this.evse(evseId);
    if (!evse) throw new Error(`Unknown connector ${evseId}`);
    this.advanceMeter(evse);
    let meterValue = this.meterValue(evse, context);
    if (meterValue.length === 0) {
      if (context === "Sample.Clock") return;
      // Nothing configured: a meter reading is still the least a charger reports.
      meterValue = this.meterValue(evse, context, ["Energy.Active.Import.Register"]);
    }
    const tx = evse.transaction;
    if (!isOcpp2(this.protocol)) {
      const send = tx ? this.callOrQueue.bind(this) : this.call.bind(this);
      await send("MeterValues", {
        connectorId: evse.id,
        ...(tx?.numericId !== undefined && { transactionId: tx.numericId }),
        meterValue,
      });
      return;
    }
    if (tx) {
      const triggerReason = context === "Trigger" ? "Trigger" : context === "Sample.Clock" ? "MeterValueClock" : "MeterValuePeriodic";
      await this.callOrQueue("TransactionEvent", {
        eventType: "Updated",
        timestamp: this.timestamp(),
        triggerReason,
        seqNo: tx.seqNo++,
        evse: { id: evse.id, connectorId: 1 },
        transactionInfo: { transactionId: tx.id, chargingState: tx.chargingState },
        meterValue,
      });
      return;
    }
    await this.call("MeterValues", { evseId: evse.id, meterValue });
  }

  /** ClockAlignedDataInterval: MeterValuesAlignedData of every connector on the clock (00:00 + n x interval). */
  startClockAlignedTimer() {
    if (this.clockTimer) clearTimeout(this.clockTimer);
    this.clockTimer = null;
    const seconds = this.config.getNumber("ClockAlignedDataInterval", 0);
    if (seconds <= 0 || this.state !== "accepted") return;
    const now = this.now().getTime();
    const intervalMs = seconds * 1000;
    const next = Math.floor(now / intervalMs) * intervalMs + intervalMs;
    this.clockTimer = setTimeout(() => {
      this.clockTimer = null;
      this.later(async () => {
        for (const evse of this.evses) {
          if (evse.status === "Charging") await this.sendMeterValues(evse.id, "Sample.Clock").catch(() => undefined);
        }
      });
      this.startClockAlignedTimer();
    }, next - now);
  }

  /** Manually set the meter and/or state of charge (UI fields). */
  setMeter(evseId: number, values: { meterWh?: number; soc?: number }) {
    const evse = this.evse(evseId);
    if (!evse) return;
    this.advanceMeter(evse);
    if (values.meterWh !== undefined) evse.meterWh = values.meterWh;
    if (values.soc !== undefined) evse.soc = Math.max(0, Math.min(100, values.soc));
    this.emitEvse(evse);
  }

  /**
   * Charging power of a connector in W (how fast its meter goes up); undefined: the connector
   * maximum. Charging profiles, the connector maximum and the EV taper still cap it.
   */
  setPower(evseId: number, powerW: number | undefined) {
    const evse = this.evse(evseId);
    if (!evse) return;
    this.advanceMeter(evse);
    evse.requestedPowerW = powerW === undefined || !Number.isFinite(powerW) ? undefined : Math.max(0, powerW);
    evse.powerW = evse.transaction ? this.drawnPowerW(evse) : 0;
    this.updateChargingState(evse, true);
    this.emitEvse(evse);
  }

  setStopReason(evseId: number, reason: string) {
    const evse = this.evse(evseId);
    if (!evse) return;
    evse.stopReason = reason;
    this.emitEvse(evse);
  }

  startMeterTimer(evse: EvseState) {
    this.stopMeterTimer(evse);
    const seconds = this.config.getNumber("MeterValueSampleInterval", 0);
    if (seconds <= 0) return;
    this.meterTimers.set(
      evse.id,
      setInterval(() => {
        this.emitEvse(evse);
        // Periodic samples only while energy is actually being delivered.
        if (this.isOnline && evse.status === "Charging") void this.sendMeterValues(evse.id).catch(() => undefined);
      }, seconds * 1000)
    );
  }

  private stopMeterTimer(evse: EvseState) {
    const timer = this.meterTimers.get(evse.id);
    if (timer) clearInterval(timer);
    this.meterTimers.delete(evse.id);
  }

  // ---- reservations ------------------------------------------------------------------------

  reserve(evse: EvseState, reservation: Reservation) {
    evse.reservation = reservation;
    evse.status = "Reserved";
    this.emitEvse(evse);
    const ms = Math.max(0, new Date(reservation.expiresAt).getTime() - this.now().getTime());
    const existing = this.reservationTimers.get(evse.id);
    if (existing) clearTimeout(existing);
    this.reservationTimers.set(
      evse.id,
      setTimeout(() => {
        this.reservationTimers.delete(evse.id);
        if (evse.reservation?.id !== reservation.id) return;
        this.log("info", `Reservation ${reservation.id} expired`);
        this.clearReservation(evse, true, "Expired");
      }, ms)
    );
  }

  clearReservation(evse: EvseState, notify: boolean, updateStatus: "Expired" | "Removed" = "Removed") {
    const reservation = evse.reservation;
    evse.reservation = undefined;
    const timer = this.reservationTimers.get(evse.id);
    if (timer) clearTimeout(timer);
    this.reservationTimers.delete(evse.id);
    if (evse.status === "Reserved") evse.status = "Available";
    this.emitEvse(evse);
    if (!notify || !this.isOnline) return;
    this.later(async () => {
      await this.sendStatus(evse).catch(() => undefined);
      if (reservation && isOcpp2(this.protocol)) {
        await this.call("ReservationStatusUpdate", { reservationId: reservation.id, reservationUpdateStatus: updateStatus }).catch(
          () => undefined
        );
      }
    });
  }

  // ---- incoming ----------------------------------------------------------------------------

  private async handleCall(action: string, params: any): Promise<any> {
    this.log("request", JSON.stringify(params), action);
    return this.dispatch(action, params);
  }

  private async dispatch(action: string, params: any): Promise<any> {
    if (this.state === "rejected") {
      throw new OcppError("SecurityError", "Registration rejected: requests are not accepted");
    }
    const handler = this.handlers[action];
    if (handler) return handler(params);
    // Version messages without a dedicated behaviour (e.g. 2.1 DER and tariff control): accept
    // and remember the request so it can be inspected.
    this.records[action] = params;
    const schema = this.catalogOrThrow.schema(action, "conf");
    return schema ? buildMinimalPayload(schema, { now: this.now, preferredEnumValues: ["Accepted"] }) : {};
  }

  private logFrame(entry: RpcFrameLog) {
    const types: Record<string, LogEntry["type"]> = {
      CALL: entry.direction === "out" ? "send" : "request",
      CALLRESULT: entry.direction === "out" ? "send" : "receive",
      CALLERROR: "error",
      CALLRESULTERROR: "error",
      SEND: "send",
      INVALID: "error",
    };
    // Incoming CALLs are logged by handleCall with their payload already.
    if (entry.kind === "CALL" && entry.direction === "in") return;
    this.log(types[entry.kind], entry.raw, entry.action);
  }
}

export interface Events {
  log: LogEntry;
  state: ConnectionState;
  evse: EvseState;
  upload: TransferState & { active: boolean };
  config: ReturnType<ConfigurationStore["all"]>;
}
